import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Navigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { useAuth } from '@/hooks/useAuth';

import { loginRequest } from '../../auth/authConfig';
import { Button } from '../../components/atoms/Button';
import Spinner from '../../components/atoms/Spinner';

interface LoginProps {
	redirectHome?: boolean;
	isLoading?: boolean;
	error?: string | null;
}

const Login = ({
	redirectHome = true,
	isLoading = false,
	error = null,
}: LoginProps) => {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();
	const { logout } = useAuth();

	const handleRedirect = () => {
		instance
			.loginRedirect({
				...loginRequest,
				prompt: 'create',
				redirectStartPage: window.location.href,
			})
			.catch((error: any) => {
				console.error(error);
			});
	};

	const handleLogout = async () => {
		try {
			await logout();
		} catch (error) {
			console.error('Erro ao fazer logout:', error);
		}
	};

	if (isAuthenticated && redirectHome) {
		return <Navigate to="/home" />;
	}

	return (
		<div className="flex h-screen items-center justify-center bg-[url(/login_background_workflow.jpg)] bg-cover">
			<form
				className="
          relative flex 
          w-[30%] 
          flex-col items-center 
          justify-center gap-6 rounded-2xl
          bg-white
          bg-contain
          px-10
          py-10
        "
				onSubmit={(e) => e.preventDefault()}
			>
				{(isAuthenticated || error) && (
					<button
						onClick={handleLogout}
						className="hover:text-red-500 absolute right-4 top-4 rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100"
						title="Sair da conta"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							className="h-5 w-5"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
							/>
						</svg>
					</button>
				)}

				<img src="/logo.png" alt="Logo" className="w-[calc(50%+1rem)]" />

				{error ? (
					<>
						<div className="text-red-700 bg-red-100 border-red-300 mb-4 w-full rounded-lg border p-4">
							<h3 className="mb-2 text-lg font-semibold">
								Erro de Autenticação
							</h3>
							<p className="text-sm">{error}</p>
						</div>
						<h1 className="max-w-[250px] text-center text-xl font-bold text-[#3F3F3F]">
							Tente fazer login novamente
						</h1>
					</>
				) : (
					<h1
						className={twMerge(
							'max-w-[200px] text-center text-xl font-bold text-[#3F3F3F]',
							isLoading && 'max-w-[250px]',
						)}
					>
						{isLoading
							? 'Seu usuário está sendo autenticado via SSO'
							: 'Faça o seu login com Origem SSO'}
					</h1>
				)}

				{isLoading ? (
					<Spinner />
				) : (
					<div className="flex w-full flex-col gap-3">
						<Button onClick={handleRedirect} className="mt-4 text-base">
							{error ? 'TENTAR NOVAMENTE' : 'ACESSAR MINHA CONTA'}
						</Button>

						{(isAuthenticated || error) && (
							<button
								onClick={handleLogout}
								className="hover:text-red-500 text-sm text-gray-600 underline transition-colors"
							>
								Sair da conta atual
							</button>
						)}
					</div>
				)}
			</form>
		</div>
	);
};

export default Login;
